import { useState } from "react";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { hint as lookup } from "@/lib/glossary";
import { deep as lookupDeep } from "@/lib/deepGlossary";

const Deep = ({ d }) => (
  <div className="space-y-1.5 text-[11px] leading-snug text-slate-300">
    {d.why && <div><span className="text-cyan-300 font-semibold">Why it matters: </span>{d.why}</div>}
    {d.how && <div><span className="text-cyan-300 font-semibold">How it works: </span>{d.how}</div>}
    {d.action && <div><span className="text-cyan-300 font-semibold">What to do with it: </span>{d.action}</div>}
    {d.example && <div className="text-slate-400 italic">Example: {d.example}</div>}
  </div>
);

// A label with a small ⓘ. Desktop: hover or keyboard-focus the icon for the explanation. Phones/tablets (no hover): tap opens a
// readable dialog with a close button. Click also opens the dialog on desktop for the long version.
// `k` = glossary key (short text; optional deep text), or pass `title`/`text`. `values` fills {placeholders} in the text.
export const Term = ({ k, title, text, values, children, className = "", iconClass = "", testid, side = "top" }) => {
  const [open, setOpen] = useState(false);
  const entry = k ? lookup(k) : null;
  const d = lookupDeep(k);
  const t = title ?? entry?.[0];
  let body = text ?? entry?.[1];
  if (!body) return children ?? null;
  if (values) body = body.replace(/\{(\w+)\}/g, (_, key) => (values[key] ?? `{${key}}`));
  const tid = testid || `term-${k || "inline"}`;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid={tid} onClick={(e) => { if (!e.currentTarget.contains(e.target) || e.target.closest("[role=dialog]")) e.stopPropagation(); }}>
      {children}
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label={`What is ${t}?`} data-testid={`${tid}-icon`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                  className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-500 hover:text-cyan-300 focus-visible:text-cyan-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-400 align-middle ${iconClass}`}>
            <Info className="w-3 h-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} sideOffset={8} collisionPadding={12} className={`apx-hint ${d ? "max-w-[340px]" : "max-w-[280px]"} px-3 py-2.5 rounded-lg text-left`} data-testid={`${tid}-tip`}>
          {t && <div className="font-display text-[12px] font-semibold text-cyan-200 mb-0.5">{t}</div>}
          <div className="text-[11px] leading-snug text-slate-300">{body}</div>
          {d && <div className="mt-1.5 pt-1.5 border-t border-slate-800"><Deep d={d} /></div>}
          <div className="mt-1.5 text-[10px] text-slate-500">Click the icon for more · educational, not financial advice.</div>
        </TooltipContent>
      </Tooltip>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#0F141C] border-slate-800 text-slate-100" data-testid={`${tid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display">{t}</DialogTitle>
            <DialogDescription className="text-slate-300 text-sm">{body}</DialogDescription>
          </DialogHeader>
          {d && <div className="text-sm"><Deep d={d} /></div>}
          <div className="text-[11px] text-slate-500 flex flex-wrap justify-between gap-2"><span>Educational information only — not financial advice. Predictions can be wrong; past results don't guarantee future outcomes.</span><Link to={k ? `/glossary#term-${k}` : "/glossary"} onClick={() => setOpen(false)} className="text-cyan-300 hover:underline whitespace-nowrap" data-testid={`${tid}-all-terms`}>{k ? "See this term in the glossary →" : "See all terms →"}</Link></div>
        </DialogContent>
      </Dialog>
    </span>
  );
};
